import { defineStore } from "pinia";
import { applyTaskToState, taskInput } from "../lib/task"
// MEME_SLOTS and the slot->field convention, so the zone count below and the
// field names it reads cannot drift apart. lib/meme.js imports nothing, so
// this adds no cycle.
import { MEME_SLOTS, memeStoreKeys } from "../lib/meme"
// One answer to "which pixels did they pick", shared with the canvas path.
import { cropRegion, turnedRegion } from "../lib/imageTransform"

// Which formats are produced unless the user says otherwise.
// One format by default, and deliberately the universal one.
//
// Most visitors are making a photo small enough to email or text, and for them
// "which formats" is not a question they have -- they want one file that opens
// wherever they send it. AVIF and WebP are what a DEVELOPER came for, and they
// arrive with the <picture> element that gives them a reason to exist.
//
// Measured cost of this default on the three demo images: the headline savings
// reads 84% instead of 87-89%, because that number averages every variant and
// the large JPEG dominates it either way. The phone file goes from about 8 KB
// to about 15 KB. Neither is a number anybody acts on, and "up to 80% and
// more" still holds.
const DEFAULT_FORMATS = ["jpeg"]

// What ticking "build <picture> elements" turns on with it: a picture element
// whose only source is a JPEG is an elaborate way to write <img>.
const PICTURE_FORMATS = ["jpeg", "webp", "avif"]

const VARIANT_SIZES = [
    {suffix: "_large", width: 1280},
    {suffix: "_small", width: 360}
]

/* Which widths a download comes in, by default: one.
 *
 * Two was the original default, from when this was a tool for building
 * responsive images -- a 1280 for desktops and a 360 for phones. That is now
 * the least common of the thirteen tasks, and the cost of keeping it as the
 * default was a decision imposed on everyone else: two JPEGs of the same
 * photograph, and a table to choose between them, for somebody who wanted to
 * email a picture.
 *
 * The second width comes back with the <picture> element, which is the case
 * that genuinely needs several -- a srcset with one entry is an elaborate way
 * to write width. */
const DEFAULT_SIZES = [VARIANT_SIZES[0]]

/* The "Best for" column, as a KEY rather than a sentence.
 *
 * The store has no business holding English. It returns
 * variants.<format>_<size> and the table resolves it, so a variant list built
 * before the catalogue loads is still correct once it has. */
function describeVariant(fmt, suffix) {
    return "variants." + fmt + (suffix === "_large" ? "_large" : "_small")
}

// Build the variant list for a set of formats.
//
// This lives at module scope, and state() below calls it, so the INITIAL state
// already holds a valid list. That matters more than it looks: file-list.js
// calls uiStore.$reset() every time a file is selected, and $reset() restores
// whatever state() returns. An empty initial imageVersions therefore left the
// app with no variants at all the moment anyone picked an image.
function buildVariants(formats, sizes = DEFAULT_SIZES) {
    const out = []
    let id = 0
    for (const fmt of formats) {
        for (const size of sizes) {
            out.push({
                id: id++,
                size: 0,
                suffix: size.suffix,
                width: size.width,
                format: fmt,
                description: describeVariant(fmt, size.suffix),
                highlight: id === 1
            })
        }
    }
    return out
}


export const useUiElements = defineStore("uiStorage", {
    state: () =>  {
        const base = {


            // --- transformation options, all of them ImageEngine directives ---

            // compression, 0 (least) to 99 (most). null means "let ImageEngine
            // decide", which is what this tool did before the slider existed.
            cmpr: null,

            // r_<deg>
            rotate: 0,

            // Neither of these is an ImageEngine directive. flip_h and
            // mirror_true both answer 412, and cr_ works but will not combine
            // with a resize, so both happen on a canvas before the upload --
            // see src/lib/imageTransform.js, which also explains the size cap.
            //
            // They therefore behave differently from everything else here:
            // changing one means re-uploading, not just re-fetching the CDN
            // with new directives.
            flipH: false,
            flipV: false,
            // A scrim: a flat colour laid over the picture at a chosen
            // opacity, so text put on top of it can be read. Zero means no
            // overlay at all and costs nothing -- see usableOverlay() in
            // lib/imageTransform.js, which is where "asked for" is decided.
            overlayColor: "#000000",
            overlayOpacity: 0,

            // Words drawn onto the picture. Empty text means no watermark and
            // costs nothing -- usableWatermark() in lib/imageTransform.js is
            // where "asked for" is decided, the same as the scrim above.
            watermarkText: "",
            watermarkPosition: "bottom-right",
            watermarkSize: 0.06,
            watermarkOpacity: 0.6,
            watermarkColor: "#ffffff",
            watermarkFont: "sans",

            // Captions, top and bottom. Two empty fields mean no captions and
            // cost nothing, the same bargain as the scrim and the watermark --
            // usableMeme() in lib/imageTransform.js is where "asked for" is
            // decided.
            //
            // The size is much larger than the watermark's 0.06 because these
            // are the point of the picture rather than a signature on it, and
            // the face is Anton rather than Impact because Impact is absent
            // from Android, iOS and most Linux. Both defaults are explained
            // where they are defined, in lib/meme.js.
            memeTop: "",
            memeBottom: "",
            /* AND TWO LABEL AREAS, which the three-label formats need.
             *
             * Named for the DEFAULT BAND each opens in, exactly as top and
             * bottom are -- lib/meme.js puts these two side by side in the
             * middle -- and not for where they have to stay. What a visitor
             * reads is Area 1 to Area 4; the slot name never reaches the page.
             *
             * Listed rather than built from an index so that a rename cannot
             * quietly produce a field that writes to nothing, which is the
             * reason MemeControl.vue lists its store keys too. */
            memeLeft: "",
            memeRight: "",
            /* HOW MANY TEXT ZONES ARE ON OFFER, and one is the answer until
             * somebody asks for two.
             *
             * Both rectangles used to be drawn the moment a picture was
             * staged. On a card that wants a caption top and bottom that is
             * right; on an ordinary photograph it is two dashed boxes asking
             * for a decision nobody made, and the second one is furniture for
             * the whole session if it is never used.
             *
             * A picture that KNOWS it wants two says so: a library card with
             * two white bars carries them in `areas`, and useLibraryPicture()
             * opens exactly as many zones as the picture declares. Everything
             * else opens one, with "add another" beside the words.
             *
             * A count rather than a set of booleans: the slots are ordered --
             * lib/meme.js names them top, bottom, left, right and places them
             * in that order -- so "how many" is the whole of the question. */
            memeZones: 1,
            /* Colour and size PER AREA.
             *
             * They were one of each, shared. A caption on the dark half of a
             * photograph and one on the light half want different colours, and
             * a long line beside a short one wants a different size -- so the
             * shared pair meant every choice was a compromise between two
             * captions. The face and the capitals are still shared: two
             * typefaces on one meme is a different sort of decision.
             */
            memeTopSize: 0.12,
            memeBottomSize: 0.12,
            memeLeftSize: 0.12,
            memeRightSize: 0.12,
            memeTopColor: "#ffffff",
            memeBottomColor: "#ffffff",
            memeLeftColor: "#ffffff",
            memeRightColor: "#ffffff",
            memeFont: "anton",
            memeUpper: false,
            /* Where each caption goes, when the visitor has said.
             *
             * FRACTIONS of the picture's width and height, {x, y, w, h}, not
             * pixels -- so one rectangle serves the stage preview, the canvas
             * that bakes it and whatever the picture is scaled to on the way
             * out. Null means the default band at the top or the bottom, which
             * is what most captions want and what nobody has to draw.
             *
             * Deliberately NOT in ALLOWED in lib/task.js: a rectangle only
             * means anything against a particular picture, so it is not
             * something a task page could sensibly arrive with.
             */
            memeTopBox: null,
            memeBottomBox: null,
            memeLeftBox: null,
            memeRightBox: null,

            // Where a PDF has got to. Two phases, because only one of them can
            // honestly be measured.
            //
            //   "uploading"  the browser is sending the file. XHR reports real
            //                progress for this, so it gets a bar.
            //   "rendering"  the server is turning pages into images. There is
            //                no progress to report -- the response arrives when
            //                it arrives -- so it gets a spinner. A bar stuck at
            //                100% while something else works is worse than no
            //                bar at all.
            //
            // null means no PDF is in flight.
            pdfPhase: null,
            // Seconds until the upload is retried, while the server's PDF
            // slots are full. Zero when nothing is waiting.
            pdfRetryIn: 0,
            pdfPercent: 0,

            // Pages, guessed from the file before it was sent -- see
            // lib/pdfPages.js. Zero means it could not be told, and every use
            // of it withholds rather than invents.
            pdfPages: 0,

            // Milliseconds left, or 0 for "no honest number". Recomputed as it
            // goes: from the measured upload rate while sending, from the page
            // count while rendering.
            pdfRemainingMs: 0,

            // The server's own count of the work, polled while it happens:
            // pages finished, pages in total, and which of the two per-page
            // phases is running. Zero total means nothing is being reported and
            // the modal falls back to its estimate.
            pdfDone: 0,
            pdfTotal: 0,
            pdfStage: "",

            // {x, y, w, h} in SOURCE pixels, or null. Single-image path only:
            // a region can only be pointed at, and there is nothing to point
            // at when ten images share one panel.
            crop: null,

            // meta_true keeps EXIF. Default off, i.e. GPS and camera data are
            // stripped -- worth saying out loud in the UI, it is a real feature.
            keepExif: false,

            // size presets. null keeps the original aspect ratio and only
            // constrains width, which is the historical behaviour.
            preset: null,
            presetList: [
                {id: "og",        label: "Open Graph",   w: 1200, h: 630},
                {id: "square",    label: "Square 1:1",   w: 1080, h: 1080},
                {id: "portrait",  label: "Portrait 4:5", w: 1080, h: 1350},
                {id: "story",     label: "Story 9:16",   w: 1080, h: 1920},
                {id: "linkedin",  label: "LinkedIn",     w: 1584, h: 396},
                {id: "wide",      label: "Wide 16:9",    w: 1600, h: 900}
            ],

            // before/after comparison slider
            showComparison: false,
            comparePosition: 50,    // percent

            files: [], // one for the single-image path, many in batch mode

            // --- batch mode ------------------------------------------------
            //
            // Picking one image keeps the whole existing single-image flow:
            // six variants, the before/after slider, the picture element. That
            // path is untouched.
            //
            // Picking several switches to this instead, which is a different
            // job rather than the same one N times. Somebody with twelve
            // holiday photos wants twelve smaller photos, not seventy-two
            // files and a picture tag. So batch produces exactly ONE output per
            // image, in one chosen format, and shows a row per image.
            //
            // It also keeps the fan-out sane: one CDN transform per image
            // rather than six, which is what makes twelve files reasonable.
            batchMode: false,
            batchFormat: "jpeg",     // universal by default: email, messaging

            // Off: one file per image, one transform per image, which is what
            // somebody emailing ten holiday photos wants.
            //
            // On: the full responsive set per image and a <picture> element for
            // each, which is what a developer putting ten images on a page
            // wants. That costs formats x 2 transforms per image instead of
            // one, so it is a deliberate choice rather than the default.
            //
            // The snippets have to reference files the developer will host, the
            // same way the single-image snippet does -- not CDN URLs. Uploads
            // here are swept within the hour, so a pasted CDN URL would 404 in
            // production. Which is why turning this on has to put every variant
            // in the zip: the markup is only usable with the files beside it.
            pictureElements: false,
            // The batch that a single image was opened out of, kept whole.
            //
            // Opening one row used to destroy the batch -- every blob revoked,
            // batchItems emptied -- so the only way back to the other rows was
            // to do the whole thing again, which for a PDF means uploading and
            // rasterising it a second time. It is set aside here instead:
            // nothing is revoked, nothing is recomputed, and going back is an
            // assignment. null means there is nothing to go back to.

            batchRunning: false,
            batchDone: 0,
            batchItems: [],
            // each item: {id, name, key, url, origName, origSize, outSize,
            //             status: 'waiting'|'working'|'done'|'failed', blob}

            // the root of all IE transformation
            s3urlRoot: "https://vd331zy7.cdn.imgeng.in/uploads/",
            // IEUrlRoot: "https://vd331zy7.cdn.imgeng.in/",

            showAddSetting : false, // show/hide additional settings
            showDownloadables : false, // show/hide additional settings
            /* NOT "100". OptionsPanel hard-codes a first option reading
             * "Full width (100%)" with value 100, so listing it here too put
             * the same choice in the menu twice -- measured on /svg-to-png,
             * the select offered 100,100,66,50,25. The labelled one is the
             * better of the pair, so this list is the reductions only. */
            percentageList : ["66", "50", "25" ], // reductions; 100 is the labelled option above
            
            optimizationInProgress: false, // useful to tell user that optimization is in progress
            optimizedImagesCount : 0,
            
            pct : 100,  // resize options. default = 100% of target screen
            
            // current uploaded image
            workableImage : {
            
                ready :  false, // initially we have no image to work with
                name :  "", // Image Name
                url : "" // workable image
            },

            //keep track of the picture element text
            //pictureElementText: "",

            // keep track of supported formats
            supportedFormats: new Set(["jpeg"]),


            // The formats on offer. Each becomes two variants (large and
            // small) when enabled, so the table and the picture element are
            // both derived from this rather than hardcoded.
            //
            // bmp and jp2 are deliberately absent: the CDN serves them, but bmp
            // came back eight times larger than the optimized JPEG, and jp2 is
            // Safari legacy. Offering formats that make files bigger would
            // contradict the point of the tool.
            formatCatalog: [
                {id: "jpeg", label: "JPEG",    on: true,
                 note: "Universal. Email, messaging, anywhere."},
                {id: "webp", label: "WebP",    on: true,
                 note: "Wide support, much smaller than JPEG."},
                {id: "avif", label: "AVIF",    on: true,
                 note: "Smallest in current browsers."},
                /* Chrome 155 shipped JPEG XL decoding (announced 6 October
                 * 2026) and Safari has read it since 17, so "few browsers read
                 * it yet" stopped being true.
                 *
                 * AND IT IS NOT "THE SMALLEST OF ALL", which the first fix left
                 * standing. Measured: photograph AVIF 47.4 / JXL 55.3 / WebP
                 * 62.5 KB; flat graphic WebP 3.6 / AVIF 5.4 / JXL 6.2. It is
                 * second on one and third on the other, and image-formats.html
                 * has published those numbers all along. The old line only
                 * worked as a contrast with AVIF's "smallest in CURRENT
                 * browsers" -- smallest if only you could use it -- and Chrome
                 * 155 collapsed that. What is true in both tables is that it
                 * beats JPEG comfortably.
                 *
                 * STILL off by default: the default
                 * is what somebody gets without choosing, and handing them a
                 * .jxl an older browser cannot open is a worse failure than a
                 * slightly larger JPEG.
                 *
                 * These `note` fields are NOT what the page renders --
                 * OptionsPanel reads t('formats.' + f.id) from the catalogue --
                 * so this one had quietly drifted. Kept in step rather than
                 * deleted, because it is the only place the catalogue's shape
                 * is readable in one screen. */
                {id: "jxl",  label: "JPEG XL", on: false,
                 note: "Much smaller than JPEG, and Chrome and Safari now read it."},
                {id: "png",  label: "PNG",     on: false,
                 note: "Lossless and keeps transparency. Bigger files."},
                /* GIF, off by default, and the note does not oversell it.
                 *
                 * It is the largest output here and the only one capped at 256
                 * colours: measured on one photograph at 800px, GIF came back
                 * 233 KB against PNG's 192 KB, JPEG's 51 KB and AVIF's 25 KB.
                 * On the reasoning above -- bmp and jp2 are absent because
                 * offering formats that make files bigger contradicts the point
                 * of the tool -- it would be excluded. It is here because it
                 * was asked for: some systems still take nothing else, and a
                 * tool that cannot produce the one format your upload form
                 * accepts is no use whatever its byte count.
                 *
                 * ANIMATION DOES SURVIVE IT, since f_agif -- see
                 * buildDirectives(). It did not when this note was written:
                 * f_gif returns a single frame, and the conclusion drawn then
                 * was that pinning any format at all costs the movement. That
                 * holds for every OTHER format (f_webp flattens it too, and
                 * f_awebp keeps it), but not for GIF. */
                {id: "gif",  label: "GIF",     on: false,
                 note: "Only 256 colours, and the biggest files. For somewhere that takes nothing else."}
            ],

            // which of the above are currently produced
            enabledFormats: [...DEFAULT_FORMATS],

            // seeded here rather than left empty, so $reset() cannot wipe it
            imageVersions: buildVariants(DEFAULT_FORMATS),

            // image from internet Model (control visibility)
            imageFromInternetModal: false,
            awaitingFetchImage: false,
            fetchedImageError: "",
            fetchedImageUrl: "",

            // subfolder name and alt attribure (needed to produce <picture> element)
            subfolderUser: "",
            altattr: "",

            // little optimization on smartphone. Let user click to visualize instructions
            showSmartphoneInstrux: false,

            // show/hide area for images fetched from the web
            showFetchedImagePreviewArea: false,

            /* Turning a clip into an animation.
             *
             * The settings and the progress both live in state(), so $reset()
             * clears them -- which is what makes dropping a second clip put
             * the previous one's error away with no code to do it, the same
             * property the animated-GIF notice relies on.
             *
             * videoWidth is the width the BROWSER encodes at. The page then
             * asks the CDN for 90% of it, because the CDN answers
             * origin-object at equal width and there is no re-encode -- and
             * that re-encode is worth about half the bytes. So the delivered
             * GIF is a little narrower than this number, and the results table
             * shows what actually came back. */
            videoWidth: 480,
            videoFps: 10,

            videoBusy: false,
            videoPhase: "",        // reading | converting | assembling
            videoDone: 0,
            videoTotal: 0,
            videoName: "",
            videoError: "",

            /* The trimmer. videoStart/videoEnd are SOURCE seconds, and the
             * selection may be wider than MAX_SECONDS -- a wide one is sampled
             * across its whole span and played at videoFps, which comes out as
             * a speed-up rather than as anything discarded. See planFrames().
             *
             * videoStrip holds the filmstrip thumbnails as data URLs. In
             * state() with everything else, so $reset() clears them: a strip
             * left over from the previous clip on the next one's trimmer would
             * be a picture of the wrong video. */
            /* A few words burnt into every frame.
             *
             * It has to be IN the bytes -- the CDN cannot draw text -- so it
             * is drawn during the conversion, which is why it is one of the
             * four things that make reconvertVideo() actually run again.
             *
             * Deliberately no live CSS preview. Everything else drawn onto a
             * picture here is implemented twice (canvas for the bytes, CSS for
             * the stage) and lib/meme.js exists to stop those two disagreeing.
             * Here the staged GIF IS the baked result, so a preview would be a
             * second renderer with nothing to gain: what you see is the file.
             */
            videoCaption: "",

            /* WHAT THE SVG LOST ON THE WAY TO BEING A PNG.
             *
             * In state() so $reset() clears them, which is what makes dropping
             * a plain logo over a text-bearing one silence the notice with no
             * code to do it -- the same thing that makes the animated-GIF
             * notice correct. Written AFTER staging, because addFiles() opens
             * with the reset that would wipe them. */
            /* HEIC IS DECODED ON THE DEVICE AND IT IS SLOW.
             *
             * A 12-megapixel photograph through libheif-wasm is seconds, not
             * milliseconds, and twenty of them is a minute -- so unlike the
             * SVG path this one has to say what it is doing. In state() so
             * $reset() clears it; written from convertHeic(), which owns the
             * whole run rather than one file. */
            heicBusy: false,
            heicPhase: "",        // loading | decoding | encoding
            heicDone: 0,
            heicTotal: 0,
            heicError: "",

            /* OPENING AN ARCHIVE, which is a different wait from decoding one.
             *
             * Inflating twenty already-compressed JPEGs is fast; READING a
             * 200 MB zip into JSZip before any of that is a second or two in
             * which the page would otherwise sit there saying nothing. Same
             * verdict as the HEIC panel beside it, and the same reason the
             * trimmer had to appear the moment a clip was chosen: a gesture
             * that produces no visible change reads as a gesture that did not
             * land.
             *
             * No counter. The whole of the cost is in loadAsync() before a
             * single entry is reachable, so a progress number would be wrong
             * exactly while it was interesting. */
            zipBusy: false,
            zipError: "",

            svgHadText: false,
            svgHadExternal: false,
            svgError: "",
            /* What goes behind the drawing. Empty is transparent, which is
             * what the canvas does anyway and what the source file says.
             * Baked into the PNG, so changing it re-rasterises rather than
             * rewriting a URL -- and, like the video settings, it is read out
             * of here and PASSED to the converter, because addFiles() opens
             * with $reset() and would otherwise wipe it mid-conversion. */
            svgBackground: "",
            /* HOW MANY PIXELS ACROSS THE PNG IS, or 0 for twice the drawing's
             * nominal size.
             *
             * A vector has no size of its own, so this is the only question
             * the page really asks -- and until now there was no way to
             * answer it upwards. The rasteriser drew at 2x and the download
             * then asked the CDN for min(1280, that), so an icon came back at
             * 48px with no way to get more and an A3 diagram rasterised at
             * 4096 was delivered at 1280. The page's own description promised
             * "at any size" the whole time.
             *
             * Baked in like the background -- it decides what the CANVAS
             * draws, not what a URL asks for -- so changing it re-rasterises,
             * and it is PASSED to the converter rather than read inside it,
             * because addFiles() opens with $reset(). */
            svgWidth: 0,
            svgConverted: false,

            videoTrimming: false,
            videoStrip: [],
            videoDuration: 0,
            videoStart: 0,
            videoEnd: 0,

            // said after the fact, about the clip that was converted
            videoFrames: 0,
            videoSeconds: 0,
            videoSpeed: 1,
            videoSped: false,

            // A staged GIF turned out to hold more than one frame. Set after
            // intake, because deciding it means reading the bytes; cleared by
            // $reset() with everything else, which is why it lives in state()
            // rather than beside the file.
            animatedGif: false,

            /* Turning a picture into text.
             *
             * The settings, and then the answer. All in state(), so $reset()
             * clears the answer along with them -- which is what makes
             * dropping a second picture put the old text away with no code to
             * do it, the same property the animated-GIF notice relies on.
             *
             * asciiCols is COLUMNS, not pixels. It is the one control whose
             * units differ from every other width on this site, because the
             * output has no pixels: see the note on the results row that
             * reported 847 columns for a 40-column picture.
             */
            asciiCols: 100,
            /* How hard to flatten the histogram before jp2a sees the picture.
             * 0 is as shot, 100 is full equalisation. 50 by default because a
             * white subject on a pale sky lands on two or three characters of
             * the ramp otherwise -- see the measurements in ascii_art.py. */
            asciiContrast: 50,
            /* OFF, and it has to be: --edge-threshold is worth a third of a
             * logo and essentially nothing on a photograph, so switching it
             * on for everybody would change the one input it cannot help.
             * The measurements are in ascii_art.py. */
            asciiEdges: 0,
            asciiRamp: "classic",
            asciiColours: false,
            /* Ask the CDN to drop the background before jp2a reads the
             * picture. Off by default and honestly named: it works on one
             * clear subject and destroys anything else -- measured, it removed
             * an entire building and left the fountain. */
            asciiNoBg: false,
            asciiDark: false,
            asciiArt: "",
            asciiRows: 0,
            asciiBytes: 0,
            asciiRunning: false,
            asciiError: "",
            asciiCopied: false,

            /* ---- one sheet out of a set, and one set out of a picture ----
             *
             * Two tools that are each other's inverse, and both are grids, so
             * their state sits together. Neither is a directive: the sheet is
             * painted from the batch's own finished output and the tiles are
             * cut on a canvas, so nothing here reaches ImageEngine.
             */

            // 0 means "work it out" -- ceil(sqrt(n)), capped, which is what a
            // contact sheet has always done. A number is what the visitor
            // asked for and wins.
            /* HOW THE PICTURES ARE ARRANGED, and the default is the one
             * that was asked for after seeing the first version.
             *
             * "collage" justifies rows so nothing is cropped and nothing is
             * padded; "fill" crops each picture to a square; "fit" letterboxes
             * it. The sheet shipped as fit-only, which on a mixed set is a
             * page of postage stamps floating in the background colour -- the
             * measurements are in lib/contactSheet.js. */
            sheetMode: "collage",
            // none | thin | wide, resolved through SHEET_GAPS. A name rather
            // than a number because the number has to scale with the sheet.
            sheetGap: "thin",
            /* The order the pictures appear in, as indexes into the finished
             * rows. Empty means "as they were dropped" -- it is only written
             * once somebody drags one, so a set nobody rearranged carries no
             * state to go stale when a row finishes late. */
            sheetOrder: [],
            /* WHERE THE CROP SITS INSIDE EACH PICTURE, keyed by the batch
             * row's own id so it follows the picture rather than the position
             * -- a focus keyed by position would jump to a different
             * photograph the moment anything was reordered.
             *
             * Only "squares, cropped" can use it: a collage crops nothing and
             * a letterboxed square shows the whole frame, so there is nothing
             * to slide. Empty means every picture is centred, which is where
             * a crop starts and where it is right about half the time -- the
             * other half is a portrait with its head cut off, which is what
             * dragging the picture in the preview exists to fix. */
            sheetFocus: {},
            sheetColumns: 0,
            /* The filenames under each picture. OFF.
             *
             * It was on, on the reasoning that identifying which picture is
             * which is the one job a contact sheet has. That is true of a
             * proof sheet and it is not true of the thing most people are
             * making, which is a collage -- and the names are the first thing
             * they then have to go and turn off. Asked for directly after
             * looking at a real sheet: "off should be the default behaviour".
             *
             * The trade is real and it is the right way round: somebody who
             * wants a proof sheet is doing something deliberate and will find
             * one switch, while somebody putting eight holiday photographs on
             * one picture never wanted `DSC_4417_1280.jpg` written across it. */
            sheetLabels: false,
            /* WHAT THE SHEET IS PAINTED ON. Empty means white, which is what a
             * sheet somebody SENDS should be -- a dark JPEG arriving because the
             * person who made it liked dark mode is a surprise at the far end.
             * Held as the hex rather than a name so drawSheet() needs no table. */
            sheetGround: "",
            sheetBusy: false,

            // 3x3, because the commonest reason to cut a picture up is an
            // Instagram grid and that is what one is.
            tileCols: 3,
            tileRows: 3,
            tileBusy: false,
            tileError: ""
        }

        // A task page boots the application already set up for the job its URL
        // names. Applied HERE rather than after mounting because $reset()
        // rebuilds from state(), and addFiles() opens with a $reset() -- so
        // anywhere else, the settings would survive exactly until the visitor
        // dropped their first image.
        // The width rule lives here, beside sizesWanted, rather than in task.js:
        // one place decides how many widths a picture element wants.
        const applied = applyTaskToState(
            base,
            (formats, wantsPair) => buildVariants(formats, wantsPair ? VARIANT_SIZES : DEFAULT_SIZES),
            base.formatCatalog.map((f) => f.id))
        /* A PAGE THAT PRESETS WORDS IN A LATER AREA OPENS ENOUGH AREAS.
         *
         * memeZones defaults to one, and a caption in a zone that is not on
         * offer is the one thing the count must never produce: it would be
         * drawn on the picture with no field and no rectangle to reach it.
         * Derived here rather than added to the registry, because the answer
         * is already in the preset and two ways of saying it would disagree.
         *
         * The LAST area with words decides, not the count of non-empty ones:
         * presetting area 3 and leaving area 2 blank still needs three open,
         * or the words in 3 are unreachable. */
        for (let i = MEME_SLOTS.length - 1; i >= 1; i--) {
            if (String(applied[memeStoreKeys(MEME_SLOTS[i]).text] || "").trim()) {
                applied.memeZones = Math.max(Number(applied.memeZones) || 1, i + 1)
                break
            }
        }
        return applied
    },

    getters: {
        /* One width, or the responsive pair. Both places that rebuild the
         * variant list read this, so they cannot disagree about it. */
        sizesWanted(state) {
            return state.pictureElements ? VARIANT_SIZES : DEFAULT_SIZES
        },


        /* Will the movement actually survive? The notice says something
         * different when it will not, so this is the whole of that decision.
         *
         * Three things have to hold at once: the file is animated, GIF is
         * among the formats being produced (every other format flattens it),
         * and nothing has been baked onto the picture in the browser.
         *
         * That last one is READ rather than recomputed. applyTransforms()
         * records originalFile and replaces file only when planTransform()
         * decided something changed, so `file !== originalFile` IS "the canvas
         * has been over it". Restating the rule here -- crop or flip or scrim
         * or watermark or logo or meme -- would be a second copy free to
         * disagree with lib/imageTransform.js, which is the mistake the meme
         * layout exists to avoid. */
        animationKept(state) {
            if (!state.animatedGif) return false
            if (!state.enabledFormats.includes("gif")) return false
            /* No file, nothing to keep. animatedGif cannot be true with an
             * empty stage today -- $reset() clears it -- but a getter that
             * answers "yes, the movement survives" about a picture that is
             * not there is the wrong answer to have lying around. */
            const f = state.files[0]
            if (!f) return false
            return !(f.originalFile && f.originalFile !== f.file)
        },

        isEmptyWorkableImage: (state) => {
            return state.files.length === 0;
            },
        
        isEmptyPackage: (state) => {
            return state.optimizedImagesCount < state.imageVersions.length;
            },

        // computed rather than read out of a fixed array, so adding a format
        // does not silently break the progress readout
        optimizedImagesPercentage: (state) => {
            if (!state.imageVersions.length) return 0
            return Math.round((state.optimizedImagesCount / state.imageVersions.length) * 100)
            },

        isUploading: (state) => {
            
            if (state.files.length === 0) {
                return false
            }
            return state.files[0].status == 'loading'
            },

        /* The folder, with exactly one trailing slash.
         *
         * The placeholder in the field reads "images/", so people type the
         * slash -- and this appended a second one, producing
         * assets/img//photo_large.avif in the markup. A doubled slash inside a
         * path is not fatal on most servers, but this is HTML somebody pastes
         * into their own site, and it reads as a mistake because it is one.
         *
         * Leading slashes are left alone: "/assets/img" is an absolute path
         * and someone may well mean it. */
        subFolderWithSlash: (state) => {
            const folder = (state.subfolderUser || "").trim().replace(/\/+$/, "")
            return folder === "" ? "" : folder + "/"
        },
 
        // calculate bandwidth of optimized image, multiply average of all versions by 1.5. 
        estimatedImageBandwidth: (state) => {
            const adjuster = 1.5 // more? less?
            // get an array of int representing the size of each optimized image
            let allSizesArray = state.imageVersions.map(a => parseInt(a.size))        
            // https://stackoverflow.com/questions/29544371/finding-the-average-of-an-array-using-js
            return adjuster * (allSizesArray.reduce((a, b) => a + b, 0) / state.imageVersions.length)
        },

        //  Comparison with size of original image
        // will yield an estimate of saved bandwidth
        estimatedSavings: (state) => {
            
                return state.workableImage.size - state.estimatedImageBandwidth
        },


        estimatedSavingsPercentage: (state) => {

            // Nothing has been uploaded yet, so there is no original size to
            // divide by and the answer is NaN. It rendered as "NaN% smaller".
            if (!Number.isFinite(state.workableImage.size) || state.workableImage.size <= 0) {
                return 0
            }
            let savingsPercentage = (state.workableImage.size - state.estimatedImageBandwidth)/state.workableImage.size
            //console.log("savingsPercentage: "+savingsPercentage)
            return Math.floor(savingsPercentage * 100)
        },

    },

    actions: {

        // Build the ImageEngine directive path for one variant.
        //
        // Everything the options panel offers is a directive, so this is the one
        // place that knows how they compose. Reference:
        // support.imageengine.io/hc/en-us/articles/360058880672-Directives
        //
        // Order matters less than compatibility: w_* wins over pc_*, and a fit
        // method needs a width and/or a height to fit into.
        buildDirectives(variant) {

            const scale = (this.pct || 100) / 100
            const isLarge = variant.suffix === "_large"
            const d = []

            if (this.rotate) {
                d.push(`r_${this.rotate}`)
            }

            /* THE TURN HAPPENS FIRST, AND EVERYTHING AFTER IT IS IN THE NEW
             * FRAME. Measured, because the written order does not decide it:
             *
             *   source image1.jpg, 4898x3265
             *   /w_400                       400x267
             *   /r_90/w_400                  400x600   <- w_ is the TURNED width
             *   /cr_600,200,0,0              600x200, corner (255,1,199)
             *   /r_90/cr_600,200,0,0         600x200, corner (164,90,107)
             *   /cr_600,200,0,0/r_90         600x200, corner (164,90,107)
             *
             * The last two are identical, so the CDN turns before it crops
             * whichever way round the string is written -- reordering the
             * directives cannot fix anything here, and both of the sizes
             * below have to be expressed in the turned frame instead.
             *
             * And the turn is CLOCKWISE: rotating the plain response -90 with
             * Pillow and comparing against /r_90 gives a mean channel
             * difference of 4.0, against 63.5 for +90. */
            const quarter = this.rotate === 90 || this.rotate === 270

            /* A CROP ON AN ANIMATED GIF IS A DIRECTIVE, NOT A CANVAS PASS.
             *
             * Two reasons, and either would be enough. A canvas holds one
             * frame, so baking a crop onto an animation FLATTENS it -- the
             * page would silently hand back a still. And on the video path
             * every canvas pass means decoding and re-encoding the whole clip,
             * which is seconds of work for a rectangle.
             *
             * Measured on a 480x270 24-frame GIF:
             *   cr_300,170,60,40/f_agif   369,026 B, 300x170, 24 frames, 80ms
             * All four arguments are required -- cr_400,300 is a 412 -- and
             * `w_` is DISCARDED when a crop is present, whichever order they
             * are written in, so the crop's own size is the output size.
             *
             * cropRegion() rather than this file's own arithmetic: the canvas
             * path uses the same function, so the two cannot disagree about
             * which pixels were picked. */
            /* The SAME answer applyTransforms() uses, and it has to be: if
             * the canvas declines the crop because this is an animation and
             * the URL declines it because animatedGif has not landed yet, the
             * crop is simply lost -- which is worse than either half alone.
             *
             * animatedGif is set after intake, once the bytes have been
             * walked. A video page does not need to wait for that: everything
             * staged there is an animation it made. */
            const animation = this.animatedGif || taskInput().kind === "video"
            const region = animation
                ? cropRegion(this.crop, this.workableImage.width, this.workableImage.height)
                : null
            if (region && region.cropped) {
                /* TURNED INTO THE ROTATED FRAME. The rectangle is dragged over
                 * the UNTURNED picture on purpose -- CropSurface sets its turn
                 * to 0 while cropping, because turning the picture under the
                 * rectangle would put the region somewhere else -- so its
                 * numbers are in the original's coordinates, and the CDN reads
                 * cr_ in the turned ones. Handing them over unchanged cuts a
                 * different part of the picture entirely.
                 *
                 * Clockwise, per the measurement above. */
                const c = turnedRegion(region, this.workableImage.width,
                                       this.workableImage.height, this.rotate)
                d.push(`cr_${c.w},${c.h},${c.x},${c.y}`)
            }

            let targetW
            let targetH = null

            if (this.preset) {
                // a preset fixes the aspect ratio, so the two sizes become the
                // preset at full size and at half size
                const p = this.presetList.find((x) => x.id === this.preset)
                if (p) {
                    const factor = isLarge ? 1 : 0.5
                    targetW = Math.round(p.w * factor * scale)
                    targetH = Math.round(p.h * factor * scale)
                }
            }

            if (targetW === undefined) {
                /* CAPPED AT THE PICTURE'S OWN WIDTH, and that fixes a bug older
                 * than the animation work.
                 *
                 * variant.width is a fixed 1280, not the width of the picture
                 * in hand, so `scale` was a percentage of a number the visitor
                 * never chose. On a 600px photo, 50% asked for w_640 -- above
                 * the source, so the CDN capped it and handed back the full
                 * width. The control did nothing and said nothing.
                 *
                 * It was found here because an animated GIF makes it loud
                 * rather than quiet: there is no re-encode without a resize
                 * (cmpr alone answers origin-object), so a GIF asked for more
                 * than its own width comes back BYTE-IDENTICAL. Measured on a
                 * 480x270 24-frame GIF: w_1280 +0%, w_479 -55%, w_432 -61%,
                 * w_360 -70%.
                 *
                 * pct 100 lands exactly where it did -- the CDN capped it
                 * anyway -- and every other value now means what it says. The
                 * width is 0 until the picture has been measured, so fall back
                 * to the variant rather than asking for w_0. */
                /* THE SOURCE WIDTH IS THE TURNED ONE. A quarter turn swaps
                 * the sides, so capping against workableImage.width asks for
                 * w_<the height> and the CDN duly shrinks the result --
                 * measured on an 800x1600 portrait turned 90 degrees, the
                 * page requested /r_90/w_800/f_jpeg for a picture that is
                 * 1600 wide, delivering it at half the linear size and a
                 * quarter of the pixels. Reported as the rotation being
                 * broken, and from the visitor's side it is: the file comes
                 * back turned and small.
                 *
                 * Invisible above 1280, which is why it survived: there the
                 * variant's own width is the smaller of the two and the cap
                 * never binds. It bites on anything narrower than that which
                 * is also taller than it is wide -- which is most of what
                 * /rotate-image exists for. */
                const natural = this.workableImage || {}
                const src = variant.ignoreSourceWidth
                    ? 0
                    : (quarter ? natural.height : natural.width)

                /* A RASTERISED DRAWING IS ALREADY THE SIZE IT WAS ASKED TO BE,
                 * so the variant's 1280 is not a sensible ceiling over it.
                 *
                 * For a photograph 1280 is a sane web default: the source is
                 * some camera's idea of big and nobody chose it. An SVG has no
                 * size of its own -- svgWidth is the whole question the page
                 * asks -- and the browser has just drawn exactly that many
                 * pixels. Capping them afterwards threw the answer away: an
                 * icon came back at 48px with no way to ask for more, and an
                 * A3 diagram rasterised at 4096 was delivered at 1280, which
                 * is 31% of what was made. The page's own description has
                 * promised "at any size" throughout.
                 *
                 * pct still applies, so "half the width" still means half.
                 * MAX_EDGE and MAX_AREA still bound the canvas, so this cannot
                 * ask for more pixels than were drawn. */
                const ceiling = this.svgConverted ? (src || variant.width)
                                                  : variant.width
                targetW = Math.floor(Math.min(ceiling, src || variant.width) * scale)
            }

            // The CDN discards w_ when a crop is present, so asking for both
            // is a URL that lies about what it does. Measured both orders.
            if (region && region.cropped) {
                // nothing: the crop above decided the size
            } else if (targetH !== null) {
                // a preset means we want the exact frame filled, so crop rather
                // than letterbox: no bars, subject fills the canvas
                d.push(`w_${targetW}`, `h_${targetH}`, "m_cropbox")
            } else {
                d.push(`w_${targetW}`)
            }

            /* GIF goes out as `agif`, and that is not a cosmetic alias.
             *
             * f_gif on an animated source returns ONE FRAME. f_agif returns
             * every frame, with the delays and the loop count intact.
             * Measured on a 480x270 24-frame GIF:
             *
             *     w_320/f_gif     38,760 B    1 frame
             *     w_320/f_agif   455,962 B   24 frames, 80ms each, loop 0
             *
             * Unconditional, because it costs nothing when there is no
             * animation to keep: on a still GIF and on a JPEG source f_agif
             * and f_gif come back BYTE-IDENTICAL (147 B and 43,420 B). So
             * there is no source-sniffing branch here to get wrong.
             *
             * OMITTING the format instead looks like it works and does not.
             * With no f_ pinned ImageEngine negotiates on the Accept header:
             * curl gets the GIF back, and a real Chrome gets 16,580 B of
             * image/avif behind a .gif filename. Measured both ways.
             *
             * f_agif is absent from the published directive list, like
             * f_awebp, f_jpeg and f_heic. */
            d.push(`f_${variant.format === "gif" ? "agif" : variant.format}`)

            if (this.cmpr !== null && this.cmpr !== "") {
                d.push(`cmpr_${this.cmpr}`)
            }

            if (this.keepExif) {
                d.push("meta_true")
            }

            return "/" + d.join("/")
        },

        // the same directives, plus dl_true so the CDN sends
        // Content-Disposition: attachment and the browser saves the file
        // without us having to synthesise a blob and click a link
        buildDownloadDirectives(variant) {
            return this.buildDirectives(variant) + "/dl_true"
        },

        // a plain, unmodified copy of the original -- used by the before/after
        // comparison so the "before" really is the before
        originalUrl() {
            return `${this.workableImage.url}?imgeng=/pass_true`
        },

        // A fresh variant list for one image in a batch. Same builder as the
        // single-image path, so the two cannot describe different sets.
        newVariantSet() {
            return buildVariants(this.enabledFormats, this.sizesWanted)
        },

        // The local filename a batch variant will have on disk, matching what
        // the single-image path writes into its snippet.
        batchVariantName(item, variant) {
            const stem = item.origName.replace(/\.[^.]+$/, "") || item.origName
            return `${stem}${variant.suffix}.${variant.format}`
        },

        // Batch reuses buildDirectives rather than composing its own string, so
        // width, quality, rotation, presets, background removal and EXIF all
        // behave identically to the single-image path. It only pins the format
        // and the large size.
        batchDirectives() {
            const large = VARIANT_SIZES[0]
            return this.buildDirectives({
                suffix: large.suffix,
                width: large.width,
                format: this.batchFormat,
                /* ONE directive string serves every row, so there is no single
                 * source width to scale against -- workableImage is the
                 * single-image path. Opting out explicitly rather than by
                 * accident: see the cap in buildDirectives(). */
                ignoreSourceWidth: true
            })
        },

        batchUrl(item) {
            return `${item.url}?imgeng=${this.batchDirectives()}`
        },

        batchDownloadUrl(item) {
            return `${item.url}?imgeng=${this.batchDirectives()}/dl_true`
        },

        // The same rendered page, to LOOK at. Identical but for dl_true, which
        // is what turns a link into a save -- so this is the one to put behind
        // "open on its own", and batchDownloadUrl stays behind Save.
        batchViewUrl(item) {
            return `${item.url}?imgeng=${this.batchDirectives()}`
        },

        // What the batch actually saved, for the one line that is worth showing
        // above a table of twelve rows.
        batchTotals() {
            // Only images whose output we actually have are counted, on both
            // sides. Summing every original against only the finished outputs
            // is how a progress bar ends up claiming a 97% saving before it has
            // done half the work -- and it would keep claiming it afterwards if
            // one image failed.
            let before = 0
            let after = 0
            let done = 0
            for (const it of this.batchItems) {
                if (it.status !== "done") {
                    continue
                }
                before += Number(it.origSize) || 0
                after += Number(it.outSize) || 0
                done++
            }
            return {
                before, after, done,
                total: this.batchItems.length,
                saved: before > after ? before - after : 0,
                percent: before ? Math.round((1 - after / before) * 100) : 0
            }
        },

        // Name an output the way the visitor will want it on disk: the original
        // stem, the width, the new extension.
        batchOutputName(item) {
            const stem = item.origName.replace(/\.[^.]+$/, "") || item.origName
            const scale = (this.pct || 100) / 100
            const width = Math.floor(VARIANT_SIZES[0].width * scale)
            const ext = this.batchFormat === "jpeg" ? "jpg" : this.batchFormat
            return `${stem}_${width}.${ext}`
        },

        isImageFormatSupported(format) {
            // console.log("Checking if browser supports " + format + "..." + this.supportedFormats.has(format))
            return this.supportedFormats.has(format)
        },

        // Rebuild the variant list from the enabled formats. Every variant
        // carries its own mutable render state (size, blob, imgEl), so this has
        // to be real state rather than a getter.
        // Rebuild the variant list from the enabled formats. Each variant
        // carries mutable render state (size, blob, imgEl), so this has to be
        // real state rather than a getter.
        rebuildImageVersions() {
            this.imageVersions = buildVariants(this.enabledFormats, this.sizesWanted)
        },

        // kept under its old name: App.vue calls this before re-optimizing
        resetImageVersionArray() {
            this.rebuildImageVersions()
        },

        // toggle one format on or off and rebuild
        // Turning on the developer output turns on the formats that give it a
        // reason to exist. Turning it off leaves them: somebody who ticked
        // AVIF on purpose should not lose it because they changed their mind
        // about the markup.
        setPictureElements(on) {
            this.pictureElements = on
            if (on) {
                const set = new Set(this.enabledFormats)
                for (const f of PICTURE_FORMATS) { set.add(f) }
                this.enabledFormats = this.formatCatalog
                    .map((f) => f.id)
                    .filter((id) => set.has(id))
                this.rebuildImageVersions()
            }
        },

        setFormatEnabled(formatId, enabled) {
            const set = new Set(this.enabledFormats)
            if (enabled) { set.add(formatId) } else { set.delete(formatId) }
            // keep catalogue order so the table is always in the same sequence
            this.enabledFormats = this.formatCatalog
                .map((f) => f.id)
                .filter((id) => set.has(id))
            if (!this.enabledFormats.length) {
                // never leave the user with nothing
                this.enabledFormats = ["jpeg"]
            }
            this.rebuildImageVersions()
        },

        
        toggleImageFromInternetModalModal() {
            this.awaitingFetchImage = false
            this.imageFromInternetModal = !this.imageFromInternetModal
            // hack. I need the URL input text field to have the focus. Not too elegant but
            // I couldn't find an event that triggers with v-show changes?
            if (this.imageFromInternetModal) {
                setTimeout(() => {
                    const inputElem = document.getElementById("imageUrlInputElement")
                    if (inputElem) {
                        // console.log(inputElem,"inputElem esiste. Proviamo a dargli il focus()")
                        inputElem.focus()
                    }
                }, 200)
            }
        },
  
    
    }
        
        
    //     resetImage() {
    //         console.log("resetting image...");
    //         this.currentImage = null;
    //         this.versions = Array(6);
    //         this.zippedFile = null;
    //     },
    // }
});